import React, { useCallback, useMemo, memo, useState, useEffect } from 'react';
import { useMap, AdvancedMarker } from '@vis.gl/react-google-maps';
import CustomMarker from './LocationMarker';
import useSupercluster, { type PointProps as SuperclusterPointProps, type ClusterProps, type CombinedFeature } from '@/hooks/useSupercluster';
import { padBounds } from '@/utils/geo';
import { useTheme } from 'next-themes';
import type { FirestoreLocation } from '@/types/firestore';
import { GeoPoint } from 'firebase/firestore';
import type { Feature as GeoJsonFeature, Point as GeoJsonPoint } from 'geojson';

// Define InputPoint locally if not exported from useSupercluster. Matching its structure
// This should be GeoJsonFeature<GeoJsonPoint, SuperclusterPointProps>
interface InputPoint extends GeoJsonFeature<GeoJsonPoint, SuperclusterPointProps> {}

// Type guard
function isClusterProperties(
  props: SuperclusterPointProps | ClusterProps
): props is ClusterProps {
  return 'cluster' in props && props.cluster === true;
}

// OKLCH parsing and interpolation functions
function parseOklch(colorString: string | null): { l: number, c: number, h: number, a?: number } | null {
  if (!colorString) return null;
  const oklchMatch = colorString.match(
    /oklch\(\s*([0-9.]+)\s+([0-9.]+)\s+([0-9.]+)(?:\s*\/\s*([0-9.]+))?\s*\)/
  );
  if (oklchMatch) {
    return {
      l: parseFloat(oklchMatch[1]),
      c: parseFloat(oklchMatch[2]),
      h: parseFloat(oklchMatch[3]),
      a: oklchMatch[4] ? parseFloat(oklchMatch[4]) : undefined,
    };
  }
  return null;
}

function interpolateOklch(
  color1: { l: number, c: number, h: number, a?: number },
  color2: { l: number, c: number, h: number, a?: number },
  t: number
): string {
  const l = color1.l * (1 - t) + color2.l * t;
  const c = color1.c * (1 - t) + color2.c * t;
  const h1 = color1.h;
  let h2 = color2.h;
  let deltaH = h2 - h1;
  if (deltaH > 180) h2 -= 360;
  else if (deltaH < -180) h2 += 360;
  deltaH = h2 - h1;
  let h = (h1 + t * deltaH);
  h = ((h % 360) + 360) % 360;
  const a1 = color1.a === undefined ? 1 : color1.a;
  const a2 = color2.a === undefined ? 1 : color2.a;
  const a = a1 * (1 - t) + a2 * t;
  if (Math.abs(a - 1) < 0.001) {
    return `oklch(${l.toFixed(4)} ${c.toFixed(4)} ${h.toFixed(2)})`;
  } else {
    return `oklch(${l.toFixed(4)} ${c.toFixed(4)} ${h.toFixed(2)} / ${a.toFixed(2)})`;
  }
}

const MAX_POINTS_FOR_GRADIENT = 120;

interface ClusterMarkerProps {
  clusterId: number;
  pointCount: number;
  position: { lat: number; lng: number };
  onClusterClick: (lng: number, lat: number, expansionZoom: number) => void;
}

const ClusterMarkerComponent = ({
  pointCount,
  position,
  onClusterClick,
}: ClusterMarkerProps) => {
  const map = useMap(); 
  const { theme: currentThemeName } = useTheme();
  const [isAnimating, setIsAnimating] = useState(true);
  const [basePrimaryColor, setBasePrimaryColor] = useState<{ l: number, c: number, h: number, a?: number } | null>(null);
  const [baseDestructiveColor, setBaseDestructiveColor] = useState<{ l: number, c: number, h: number, a?: number } | null>(null);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const primaryCss = getComputedStyle(document.documentElement).getPropertyValue('--chart-2').trim();
      const destructiveCss = getComputedStyle(document.documentElement).getPropertyValue('--destructive').trim();
      setBasePrimaryColor(parseOklch(primaryCss));
      setBaseDestructiveColor(parseOklch(destructiveCss));
    }
  }, [currentThemeName]);

  const size = React.useMemo(() => Math.min(60, 30 + (pointCount / Math.max(50, pointCount * 2)) * 30), [pointCount]);
  const color = React.useMemo(() => {
    if (basePrimaryColor && baseDestructiveColor) {
      const t = Math.min(1, pointCount / MAX_POINTS_FOR_GRADIENT);
      return interpolateOklch(basePrimaryColor, baseDestructiveColor, t);
    }
    if (pointCount < 10) return 'var(--chart-5)'; 
    else if (pointCount < 25) return 'var(--accent)'; 
    else if (pointCount < 50) return 'var(--chart-4)';
    else return 'var(--destructive)';
  }, [pointCount, basePrimaryColor, baseDestructiveColor]);

  const handleClick = useCallback(() => {
    const currentMapZoom = map?.getZoom() ?? 0;
    const expansionZoom = Math.min(currentMapZoom + 2, 20); 
    onClusterClick(position.lng, position.lat, expansionZoom);
  }, [map, position.lng, position.lat, onClusterClick]);
  
  useEffect(() => {
    setIsAnimating(true); 
    const timer = setTimeout(() => setIsAnimating(false), 10); 
    return () => clearTimeout(timer);
  }, [position.lat, position.lng]);

  return (
    <AdvancedMarker position={{ lat: position.lat, lng: position.lng }} onClick={handleClick}>
      <div
        style={{
          width: `${size}px`,
          height: `${size}px`,
          borderRadius: '50%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: color,
          boxShadow: '0 0 0 3px white',
          fontSize: `${Math.min(18, 12 + (pointCount / 100) * 6)}px`,
          fontWeight: 'bold',
          color: 'white',
          transition: 'transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.3s ease-out',
          border: '1.5px solid rgba(255,255,255,0.6)',
          transform: isAnimating ? 'scale(0.5)' : 'scale(1)',
          opacity: isAnimating ? 0 : 1,
          cursor: 'pointer',
        }}
      >
        {pointCount}
      </div>
    </AdvancedMarker>
  );
};

const ClusterMarker = memo(ClusterMarkerComponent);
ClusterMarker.displayName = 'ClusterMarker';

interface LocationSelectMapMarkersProps {
  locations: FirestoreLocation[];
  onMarkerClick: (locationId: string) => void;
  onClusterClick: (lng: number, lat: number, expansionZoom: number) => void; 
  mapBounds: google.maps.LatLngBoundsLiteral | null;
  zoom: number;
  selectedLocationIds: string[];
}

const LocationSelectMapMarkers: React.FC<LocationSelectMapMarkersProps> = ({
  locations,
  onMarkerClick,
  onClusterClick,
  mapBounds,
  zoom,
  selectedLocationIds
}) => {

  const points: InputPoint[] = useMemo(() => {
    return locations.map(loc => {
      let lat = 0, lng = 0;
      const coords = loc.coordinates as any;
      if (coords instanceof GeoPoint) {
        lat = coords.latitude;
        lng = coords.longitude;
      } else if (coords && typeof coords.latitude === 'number' && typeof coords.longitude === 'number') {
        lat = coords.latitude;
        lng = coords.longitude;
      }
      // Ensure the object conforms to InputPoint (GeoJsonFeature<GeoJsonPoint, SuperclusterPointProps>)
      return {
        type: 'Feature' as const, // Use string literal type
        properties: { 
          // Ensure these properties match SuperclusterPointProps from useSupercluster.ts
          id: loc.id!,
          name: loc.name,
          locationType: loc.locationType,
          address: loc.address || '',
          // averageRating, ratingCount, drinkCount, lastSightingDate are optional in SuperclusterPointProps
          // cluster is not part of SuperclusterPointProps, it's for ClusterProps
        },
        geometry: { 
          type: 'Point' as const, // Use string literal type
          coordinates: [lng, lat] 
        }
      };
    });
  }, [locations]);

  const paddedBounds = useMemo(() => padBounds(mapBounds, 0.2), [mapBounds]);
  const clusters: CombinedFeature[] = useSupercluster(points, zoom, paddedBounds);

  return (
    <>
      {clusters.map((feature: CombinedFeature) => {
        const [lng, lat] = feature.geometry.coordinates;
        const props = feature.properties;

        if (isClusterProperties(props)) {
          const key = props.key!;
          return (
            <ClusterMarker
              key={key}
              clusterId={props.cluster_id}
              pointCount={props.point_count}
              position={{ lat, lng }}
              onClusterClick={onClusterClick} 
            />
          );
        } else {
          // Ensure props here are treated as SuperclusterPointProps
          const pointProps = props as SuperclusterPointProps;
          const key = pointProps.key!;
          const clickHandler = pointProps.id ? () => onMarkerClick(pointProps.id!) : undefined;
          const isSelected = pointProps.id ? selectedLocationIds.includes(pointProps.id) : false;
          
          return (
            <CustomMarker
              key={`${key}-${pointProps.locationType}`}
              locationId={pointProps.id}
              pointProps={pointProps} 
              lat={lat}
              lng={lng}
              onClick={clickHandler}
              isSelected={isSelected}
            />
          );
        }
      })}
    </>
  );
};

export default memo(LocationSelectMapMarkers); 